import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  FileText, Send, CheckCircle, Clock, Users, 
  Search, RefreshCw, Eye, Plus, PenTool, Download
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [feePercentage, setFeePercentage] = useState(7);
  const [creating, setCreating] = useState(false);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/contracts?${params.toString()}`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchDrivers = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/drivers?${params.toString()}`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/contracts/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, [fetchContracts]);

  useEffect(() => {
    if (createOpen) {
      fetchDrivers();
    }
  }, [createOpen, searchQuery]);

  const createContract = async () => {
    if (!selectedDriverId) {
      toast.error('Select a driver');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/contracts`, {
        driver_id: selectedDriverId,
        fee_percentage: feePercentage / 100
      });
      
      toast.success('Contract created successfully');
      setCreateOpen(false);
      setSelectedDriverId('');
      fetchContracts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    } finally {
      setCreating(false);
    }
  };

  const sendContract = async (contractId) => {
    try {
      await axios.put(`${API}/contracts/${contractId}/send`);
      toast.success('Contract sent to driver');
      fetchContracts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send contract');
    }
  };

  const signContract = async (contractId) => {
    try {
      await axios.put(`${API}/contracts/${contractId}/sign`);
      toast.success('Contract marked as signed');
      fetchContracts();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update contract');
    }
  };

  const viewContract = async (contractId) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}`);
      setSelectedContract(response.data);
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load contract');
    }
  };

  const downloadContract = (contract) => {
    const blob = new Blob([contract.html_content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contract_${contract.driver_name.replace(/\s/g, '_')}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-500/15 text-slate-400',
      sent: 'bg-amber-500/15 text-amber-500',
      signed: 'bg-emerald-500/15 text-emerald-500',
      expired: 'bg-red-500/15 text-red-500',
    };
    return styles[status] || styles.draft;
  };

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card className="glass-card" data-testid={`contract-stat-${title.toLowerCase()}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-black font-heading text-foreground mt-1">{value}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="contracts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Contracts</h1>
            <p className="text-muted-foreground mt-2">Manage dispatch service agreements with owner-operators</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { fetchContracts(); fetchStats(); }} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="glow-button" data-testid="create-contract-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Contract
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md glass-card">
                <DialogHeader>
                  <DialogTitle>Create Dispatch Agreement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Search Driver</Label>
                    <Input
                      placeholder="Search by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="mt-2"
                      data-testid="driver-search"
                    />
                  </div>
                  
                  <div>
                    <Label>Select Driver</Label>
                    <div className="mt-2 max-h-48 overflow-y-auto space-y-2 border border-border rounded-lg p-2">
                      {drivers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {searchQuery ? 'No drivers found' : 'Search for a driver'}
                        </p>
                      ) : (
                        drivers.map((driver) => (
                          <div
                            key={driver.id}
                            className={`p-2 rounded cursor-pointer transition-colors ${
                              selectedDriverId === driver.id 
                                ? 'bg-primary/10 ring-1 ring-primary' 
                                : 'hover:bg-accent/50'
                            }`}
                            onClick={() => setSelectedDriverId(driver.id)}
                          >
                            <p className="text-sm font-medium">{driver.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {driver.mc_number || driver.dot_number || 'No MC/DOT'}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Dispatch Fee (%)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={feePercentage}
                      onChange={(e) => setFeePercentage(Number(e.target.value))}
                      className="mt-2"
                      data-testid="fee-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Percentage of gross load revenue</p>
                  </div>

                  <Button 
                    className="w-full glow-button" 
                    onClick={createContract}
                    disabled={creating || !selectedDriverId}
                    data-testid="submit-contract"
                  >
                    {creating ? 'Creating...' : 'Generate Contract'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={FileText} title="Total" value={stats.total} color="#3B82F6" />
            <StatCard icon={Clock} title="Draft" value={stats.draft} color="#64748B" />
            <StatCard icon={Send} title="Sent" value={stats.sent} color="#F59E0B" />
            <StatCard icon={CheckCircle} title="Signed" value={stats.signed} color="#10B981" />
          </div>
        )}

        {/* Filters */}
        <Card className="glass-card">
          <CardContent className="p-4">
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 rounded-lg bg-background border border-border text-foreground text-sm"
                data-testid="contract-status-filter"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="signed">Signed</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Contracts List */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Contracts ({contracts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : contracts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-heading text-foreground mb-2">No Contracts Yet</h3>
                <p className="text-sm text-muted-foreground mb-6">Create your first dispatch service agreement</p>
                <Button onClick={() => setCreateOpen(true)} className="glow-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Contract
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {contracts.map((contract) => (
                  <div
                    key={contract.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-background/50 hover:bg-accent/50 transition-colors"
                    data-testid={`contract-row-${contract.id}`}
                  >
                    <div className="p-2 rounded-lg bg-primary/15">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-foreground truncate">{contract.driver_name}</p>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-md ${getStatusBadge(contract.status)}`}>
                          {contract.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>MC: {contract.mc_number || 'N/A'}</span>
                        <span>Fee: {(contract.fee_percentage * 100).toFixed(0)}%</span>
                        <span>{new Date(contract.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewContract(contract.id)}
                        data-testid={`view-contract-${contract.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      {contract.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendContract(contract.id)}
                          data-testid={`send-contract-${contract.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </Button>
                      )}
                      
                      {contract.status === 'sent' && (
                        <Button
                          size="sm"
                          className="glow-button"
                          onClick={() => signContract(contract.id)}
                          data-testid={`sign-contract-${contract.id}`}
                        >
                          <PenTool className="w-4 h-4 mr-1" />
                          Mark Signed
                        </Button>
                      )}
                      
                      {contract.status === 'signed' && (
                        <span className="text-xs text-emerald-500 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contract Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto glass-card">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Contract Preview</span>
                {selectedContract && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => downloadContract(selectedContract)}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedContract && (
              <div 
                className="bg-white text-black rounded-lg p-4 mt-4"
                dangerouslySetInnerHTML={{ __html: selectedContract.html_content }}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ContractsPage;
